/**
 * ImageBubbleMenu — bară flotantă deasupra imaginii (NU pe cursor) cu:
 *   - 3 align buttons (left / center / right) în stil Docmost
 *   - slider pentru width (10-100% din lățimea editor-ului, salvat în px)
 *
 * Poziționare: getReferenceClientRect întoarce rect-ul wrapper-ului
 * NodeView (data-asset-id pe IMG e ascuns în React NodeView).
 */
import { useEffect, useRef, useState } from 'react';
import { BubbleMenu, type Editor } from '@tiptap/react';
import { useTranslation } from 'react-i18next';

import type { ImageAlign } from '@/features/notes/AssetImage';

interface Props {
  editor: Editor;
}

/** Lățimea editabilă (în px) — referința pentru conversia slider % → px. */
function getEditorContentWidth(editor: Editor): number {
  const dom = editor.view.dom as HTMLElement;
  return dom?.clientWidth ?? 800;
}

/** Rect-ul imaginii selectate din DOM, pentru poziționarea popover-ului. */
function getImageRect(editor: Editor): DOMRect | null {
  const { $from } = editor.state.selection;
  const pos = $from.pos;
  // Nodul e atom → nodeDOM la poziția curentă întoarce wrapper-ul React.
  const dom = editor.view.nodeDOM(pos) as HTMLElement | null;
  if (dom && typeof dom.getBoundingClientRect === 'function') {
    // Preferăm rect-ul elementului <img> interior pentru un centering mai curat.
    const img = dom.querySelector('img');
    const target = img ?? dom;
    return target.getBoundingClientRect();
  }
  return null;
}

const MIN_PERCENT = 10;
const MAX_PERCENT = 100;

export default function ImageBubbleMenu({ editor }: Props) {
  const { t } = useTranslation();
  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor }) => editor.isActive('assetImage')}
      tippyOptions={{
        placement: 'top',
        duration: 120,
        offset: [0, 8],
        getReferenceClientRect: () => {
          const rect = getImageRect(editor);
          if (rect) return rect;
          const { from } = editor.state.selection;
          const start = editor.view.coordsAtPos(from);
          return new DOMRect(start.left, start.top, 0, 0);
        },
      }}
    >
      <div
        className="flex items-center gap-1 rounded-md px-2 py-1"
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        }}
      >
        <AlignBtn editor={editor} value="left" title={t('notes_blocks.image.align_left')}>
          <AlignLeftIcon />
        </AlignBtn>
        <AlignBtn editor={editor} value="center" title={t('notes_blocks.image.align_center')}>
          <AlignCenterIcon />
        </AlignBtn>
        <AlignBtn editor={editor} value="right" title={t('notes_blocks.image.align_right')}>
          <AlignRightIcon />
        </AlignBtn>
        <Sep />
        <WidthSlider editor={editor} />
        <Sep />
        <IconBtn
          onClick={() => editor.chain().focus().deleteSelection().run()}
          title={t('notes_blocks.image.delete')}
          danger
        >
          <TrashXIcon />
        </IconBtn>
      </div>
    </BubbleMenu>
  );
}

function AlignBtn({
  editor,
  value,
  title,
  children,
}: {
  editor: Editor;
  value: ImageAlign;
  title: string;
  children: React.ReactNode;
}) {
  const current = editor.getAttributes('assetImage').align ?? 'center';
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() =>
        editor.chain().focus().updateAttributes('assetImage', { align: value }).run()
      }
      title={title}
      aria-label={title}
      className="w-8 h-8 grid place-items-center rounded transition-colors"
      style={{
        color: active ? 'var(--bg)' : 'var(--text-muted)',
        background: active ? 'var(--accent)' : 'transparent',
      }}
      onMouseEnter={(e) => {
        if (active) return;
        e.currentTarget.style.color = 'var(--text)';
        e.currentTarget.style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        if (active) return;
        e.currentTarget.style.color = 'var(--text-muted)';
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

/**
 * Slider 10-100% din lățimea editor-ului content. Salvează valoarea în px
 * pe atributul `width` al nodului. Se resetează la fiecare selecție nouă
 * pentru a reflecta valoarea reală a imaginii curente.
 */
function WidthSlider({ editor }: { editor: Editor }) {
  const { t } = useTranslation();
  const attrs = editor.getAttributes('assetImage');
  const currentPx = typeof attrs.width === 'number' ? attrs.width : null;
  const editorWidth = getEditorContentWidth(editor);
  const computedPercent = currentPx
    ? Math.max(MIN_PERCENT, Math.min(MAX_PERCENT, Math.round((currentPx / editorWidth) * 100)))
    : MAX_PERCENT;

  const [value, setValue] = useState(computedPercent);
  const lastPxRef = useRef<number | null>(currentPx);

  // Resync la change-ul selecției (altă imagine, alt width)
  useEffect(() => {
    if (currentPx !== lastPxRef.current) {
      setValue(computedPercent);
      lastPxRef.current = currentPx;
    }
  }, [currentPx, computedPercent]);

  const commit = (percent: number) => {
    const px = Math.round((percent / 100) * editorWidth);
    editor.chain().focus().updateAttributes('assetImage', { width: px }).run();
    lastPxRef.current = px;
  };

  return (
    <div className="flex items-center gap-2 px-1">
      <input
        type="range"
        min={MIN_PERCENT}
        max={MAX_PERCENT}
        step={1}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        onMouseUp={() => commit(value)}
        onKeyUp={() => commit(value)}
        onTouchEnd={() => commit(value)}
        className="w-24 accent-[var(--accent)]"
        aria-label={t('notes_blocks.image.width_label')}
        title={t('notes_blocks.image.width_title', { value })}
      />
      <span
        className="font-mono text-[11px] tabular-nums w-9 text-right"
        style={{ color: 'var(--text-dim)' }}
      >
        {value}%
      </span>
    </div>
  );
}

function IconBtn({
  onClick,
  title,
  children,
  danger,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="w-8 h-8 grid place-items-center rounded transition-colors"
      style={{ color: danger ? 'var(--danger)' : 'var(--text-muted)' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = danger ? 'var(--danger)' : 'var(--text)';
        e.currentTarget.style.background = danger ? 'var(--danger-bg)' : 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = danger ? 'var(--danger)' : 'var(--text-muted)';
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

function Sep() {
  return (
    <span
      aria-hidden
      className="w-px h-4 self-center mx-1"
      style={{ background: 'var(--border-strong)' }}
    />
  );
}

// ============================================================
// Icons — Tabler inline (MIT), 18×18 stroke currentColor.
// Layout-align-left / center / right din Docmost exemplu.
// ============================================================

const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function AlignLeftIcon() {
  return (
    <svg {...iconProps}>
      <path d="M4 4l0 16" />
      <path d="M8 9m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v2a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z" />
    </svg>
  );
}

function AlignCenterIcon() {
  return (
    <svg {...iconProps}>
      <path d="M12 4l0 5" />
      <path d="M12 15l0 5" />
      <path d="M6 9m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v2a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z" />
    </svg>
  );
}

function AlignRightIcon() {
  return (
    <svg {...iconProps}>
      <path d="M20 4l0 16" />
      <path d="M4 9m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v2a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z" />
    </svg>
  );
}

function TrashXIcon() {
  return (
    <svg {...iconProps}>
      <path d="M4 7h16" />
      <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
      <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
      <path d="M10 12l4 4m0 -4l-4 4" />
    </svg>
  );
}
